/// <reference types="vite/client" />
import { createClient } from '@supabase/supabase-js'
import type { Database } from '@muaa/shared'
import { resolveActiveBackend } from '@muaa/shared'
import { getActiveEnv } from './adminEnv'
import { getBootDecision, TENANTS, DEFAULT_TENANT_SLUG } from './tenants'
import { DEV_SWITCH_ENABLED, readCachedPointerSync, fetchDevPointer, writeCachedPointer } from './devRuntimeFetch'

// Resolve the tenant from the hostname. For a redirect/unknown host we still create a
// (harmless) client against the default tenant — main.tsx never mounts the app in those
// cases, so this client is never actually used there.
const boot = getBootDecision()
const tenant = boot.kind === 'render' ? boot.tenant : TENANTS[DEFAULT_TENANT_SLUG]

// Baked env (prod by default; demo when the header toggle selected it).
const baked = getActiveEnv()

// Prod points at the resolved TENANT's project; demo stays the shared demo backend.
const prodBase = baked.isProd
  ? { url: tenant.url, anonKey: tenant.anonKey }
  : { url: baked.url, anonKey: baked.anonKey }

// Resolve against the control plane using the CACHED pointer for a synchronous boot.
const target = resolveActiveBackend({
  devModeEnabled: DEV_SWITCH_ENABLED,
  pointer: null,
  cached: readCachedPointerSync(),
  prod: prodBase,
})

// Guard: an empty baked env (missing VITE_SUPABASE_* / tenant url) makes createClient
// throw at import — a white screen on every route. Fall back to a clearly-invalid
// placeholder so the app still renders (sign-in fails against the placeholder) and log
// why, instead of hard-crashing the bundle.
const hasConfig = !!target.url && !!target.anonKey
if (!hasConfig) {
  console.error(
    '[tenants] Resolved tenant is missing a Supabase url/anonKey — check the tenant registry and VITE_SUPABASE_* build env. Rendering in a degraded state; sign-in will not work.',
  )
}

export const supabaseClient = createClient<Database>(
  hasConfig ? target.url : 'https://missing-tenant-config.invalid',
  hasConfig ? target.anonKey : 'missing-tenant-config',
  { global: { headers: target.headers } },
)

/**
 * The Supabase URL this build actually resolved to at import time (accounting for the
 * resolved tenant and the dev-switch pointer). The header's DEV/LIVE badge derives its
 * state from this so it reflects the real data source. Snapshotted at boot.
 */
export const ACTIVE_BACKEND_URL = target.url

// Background-refresh the live pointer; reload only when the resolved target CHANGED
// (the diff guard prevents a reload loop). Only runs in dev-switch builds (never in prod).
if (DEV_SWITCH_ENABLED) {
  void fetchDevPointer(prodBase.url, prodBase.anonKey).then((live) => {
    if (!live) return
    writeCachedPointer(live)
    const next = resolveActiveBackend({ devModeEnabled: true, pointer: live, cached: null, prod: prodBase })
    if (next.url !== target.url || next.anonKey !== target.anonKey) window.location.reload()
  })
}
